import { captureSurface, waitForUiFrames } from './capture'
import { emitExportTelemetry } from './telemetry'
import {
  ExportAdapterError,
  type ExportAdapterErrorCode,
  type PdfInput,
  type PdfOptions,
  type PdfWatermark,
} from './types'

import { toApiDate } from '@/shared/lib/date/toApiDate'
import { toSnakeCase } from '@/shared/lib/string'

const DEFAULT_MARGIN_MM = 8
const CANVAS_SYSTEM_COLOR = 'Canvas'
const PDF_EXPORT_FAILED = 'pdf-export-failed' satisfies ExportAdapterErrorCode
const DEFAULT_WATERMARK: Required<PdfWatermark> = { imageSrc: '/orphex.png', opacity: 0.06 }

function loadImage(src: string, timeout = 3000): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    const timer = setTimeout(() => reject(new Error('Image load timeout')), timeout)
    image.onload = () => {
      clearTimeout(timer)
      resolve(image)
    }
    image.onerror = () => {
      clearTimeout(timer)
      reject(new Error('Image load failed'))
    }
    image.src = src
  })
}

async function applyWatermark(
  canvas: HTMLCanvasElement,
  configured: PdfWatermark | undefined,
): Promise<void> {
  const watermark = { ...DEFAULT_WATERMARK, ...configured }
  const context = canvas.getContext('2d')
  if (!context) return
  try {
    const image = await loadImage(watermark.imageSrc)
    const width = canvas.width * 0.22
    const height = (image.height / image.width) * width
    context.save()
    context.globalAlpha = watermark.opacity
    context.drawImage(
      image,
      (canvas.width - width) / 2,
      (canvas.height - height) / 2,
      width,
      height,
    )
    context.restore()
  } catch {
    // The legacy watermark is intentionally best-effort; its failure never blocks the file.
  }
}

function nextSliceHeight(
  offsetY: number,
  canvasHeight: number,
  sourcePageHeight: number,
  breakpoints: readonly number[],
): number {
  const remaining = canvasHeight - offsetY
  const rawHeight = Math.min(sourcePageHeight, remaining)
  const minimumPreferred = Math.floor(sourcePageHeight * 0.65)
  const preferred = breakpoints.filter(
    (point) => point > offsetY + minimumPreferred && point < offsetY + rawHeight - 24,
  )
  return Math.max(1, Math.min((preferred.at(-1) ?? offsetY + rawHeight) - offsetY, remaining))
}

function canvasBackground(input: PdfInput, configured?: string): string {
  if (configured) return configured
  const exportPage = getComputedStyle(document.documentElement)
    .getPropertyValue('--ui-color-export-page')
    .trim()
  if (exportPage) return exportPage
  const element = input instanceof HTMLElement ? input : Array.isArray(input) ? input[0] : undefined
  if (element === undefined) return CANVAS_SYSTEM_COLOR
  return getComputedStyle(element).backgroundColor || CANVAS_SYSTEM_COLOR
}

function pageCanvas(
  source: HTMLCanvasElement,
  offsetY: number,
  height: number,
  fill: string,
): HTMLCanvasElement {
  const page = document.createElement('canvas')
  page.width = source.width
  page.height = height
  const context = page.getContext('2d')
  if (!context) throw new ExportAdapterError(PDF_EXPORT_FAILED, 'Canvas context is unavailable.')
  context.fillStyle = fill
  context.fillRect(0, 0, page.width, page.height)
  context.drawImage(source, 0, offsetY, source.width, height, 0, 0, source.width, height)
  return page
}

function safeFilename(name: string): string {
  const key = toSnakeCase(name || 'report')
    .replace(/[^a-z0-9_]/gu, '_')
    .replace(/_+/gu, '_')
    .replace(/^_|_$/gu, '')
  const today = (toApiDate(new Date()) ?? 'export').replaceAll('-', '_')
  const hash = `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
  return `${key || 'report'}_${today}_${hash}.pdf`
}

async function loadPdfVendor() {
  try {
    return (await import('jspdf')).jsPDF
  } catch (cause) {
    throw new ExportAdapterError('vendor-load-failed', 'The PDF writer could not be loaded.', cause)
  }
}

/** Pure lazy PDF writer: captures an element or writes an existing canvas as a real file. */
export async function exportPdf(
  input: PdfInput,
  filename: string,
  options: PdfOptions = {},
): Promise<void> {
  options.onProgress?.({ phase: 'preparing', pct: 0 })
  try {
    await waitForUiFrames(2)
    await options.waitFor?.()
    if (document.fonts?.ready) await document.fonts.ready

    options.onProgress?.({ phase: 'rendering', pct: 25 })
    const captured =
      input instanceof HTMLCanvasElement
        ? { canvas: input, breakpoints: [] as readonly number[] }
        : await captureSurface(input, options.capture ?? {})
    const { canvas, breakpoints } = captured
    if (canvas.width <= 0 || canvas.height <= 0) {
      throw new ExportAdapterError(PDF_EXPORT_FAILED, 'The captured PDF surface is empty.')
    }
    if (options.watermark !== null) await applyWatermark(canvas, options.watermark)

    options.onProgress?.({ phase: 'writing', pct: 75 })
    const Pdf = await loadPdfVendor()
    const pdf = new Pdf({
      orientation: options.orientation === 'landscape' ? 'l' : 'p',
      unit: 'mm',
      format: 'a4',
      compress: true,
      putOnlyUsedFonts: true,
    })
    const margin = options.margin ?? DEFAULT_MARGIN_MM
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const printableWidth = pageWidth - margin * 2
    const printableHeight = pageHeight - margin * 2
    const sourcePageHeight = Math.max(
      1,
      Math.floor((printableHeight / printableWidth) * canvas.width),
    )
    const fill = canvasBackground(input, options.capture?.backgroundColor)
    let offsetY = 0
    let pageIndex = 0

    while (offsetY < canvas.height) {
      const sliceHeight = nextSliceHeight(offsetY, canvas.height, sourcePageHeight, breakpoints)
      const page = pageCanvas(canvas, offsetY, sliceHeight, fill)
      const renderedHeight = (sliceHeight * printableWidth) / canvas.width
      if (pageIndex > 0) pdf.addPage()
      pdf.addImage(
        page.toDataURL('image/jpeg', 0.84),
        'JPEG',
        margin,
        margin,
        printableWidth,
        renderedHeight,
        undefined,
        'FAST',
      )
      offsetY += sliceHeight
      pageIndex += 1
    }

    pdf.save(safeFilename(filename))
    emitExportTelemetry(options.telemetry, options.onTelemetry)
    options.onProgress?.({ phase: 'done', pct: 100 })
  } catch (cause) {
    options.onProgress?.({ phase: 'error' })
    if (cause instanceof ExportAdapterError) throw cause
    throw new ExportAdapterError(PDF_EXPORT_FAILED, 'The PDF could not be generated.', cause)
  }
}
